import {AlertTriangle, Car, FileText, PhoneCall, Zap} from "lucide-react";
import {useMemo} from "react";
import {Link} from "react-router-dom";

import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";
import {DarSheet} from "@/src/modules/dars/components/dar-sheet";
import {DispatchLogsSheet} from "@/src/modules/dispatch-logs/components/sheet";
import {IncidentSheet} from "@/src/modules/incidents/components";
import {LocationRequiredDialog} from "@/src/modules/core/components";
import {useModal} from "@/src/modules/core/hooks";
import {useLocationGatedOpen} from "@/src/modules/core/hooks/use-location-gated-open";
import {useGetHasPermissions} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {
  canQuickActionDar,
  canQuickActionDispatchLog,
  canQuickActionIncident,
  canQuickActionParking,
} from "@/src/modules/officer/lib/quick-action-visibility";

import {
  DISPATCH_DESK_BLOCK_HEADER_CLASS,
  DISPATCH_DESK_BLOCK_TITLE_CLASS,
  DISPATCH_DESK_CARD_CLASS,
  DISPATCH_DESK_CONTENT_TEXT_CLASS,
  DispatchDeskHeaderIconRow,
} from "./dispatch-desk-card";

type DispatchQuickActionsProps = {
  isOnActiveShift: boolean;
  requireActiveShift?: boolean;
  className?: string;
};

export function getDispatchSafelistingsPanelId() {
  return "dispatch-safelistings-panel";
}

type QuickAction = {
  key: string;
  label: string;
  icon: typeof FileText;
  onClick?: () => void;
  href?: string;
};

const ACTION_BUTTON_CLASS = "h-9 justify-center gap-1.5 px-2";

export function DispatchQuickActions({
  isOnActiveShift,
  requireActiveShift = true,
  className,
}: DispatchQuickActionsProps) {
  const {modalKey, onOpenModal, onCloseModal} = useModal();
  const {requestOpen, locationDialogProps, isCheckingLocation} = useLocationGatedOpen();
  const userProperty = useStoredUserProperty();
  const getHasPermission = useGetHasPermissions();

  const isDarActive = canQuickActionDar(getHasPermission);
  const isIncidentsActive = canQuickActionIncident(getHasPermission);
  const canDispatchLog = canQuickActionDispatchLog(getHasPermission);
  const canParkingEnforcement = canQuickActionParking(getHasPermission);

  const actionsDisabled =
    (requireActiveShift && (!isOnActiveShift || isCheckingLocation)) || !userProperty?.id;

  const openGatedModal = (key: string) => {
    void requestOpen(() => onOpenModal(key));
  };

  const actions = useMemo(
    () =>
      [
        isIncidentsActive
          ? {
              key: "incident",
              label: "Log incident",
              icon: AlertTriangle,
              onClick: () => openGatedModal("incident-sheet"),
            }
          : null,
        isDarActive
          ? {
              key: "dar",
              label: "DAR",
              icon: FileText,
              onClick: () => openGatedModal("dar-sheet"),
            }
          : null,
        canDispatchLog
          ? {
              key: "dispatch-log",
              label: "Dispatch log",
              icon: PhoneCall,
              onClick: () => openGatedModal("dispatch-log-sheet"),
            }
          : null,
        canParkingEnforcement
          ? {
              key: "parking",
              label: "Parking",
              icon: Car,
              href: "/parking-enforcement",
            }
          : null,
      ].filter(Boolean) as QuickAction[],
    [canDispatchLog, canParkingEnforcement, isDarActive, isIncidentsActive],
  );

  if (actions.length === 0) return null;

  return (
    <>
      <Card className={cn(DISPATCH_DESK_CARD_CLASS, "shrink-0", className)}>
        <CardHeader className={DISPATCH_DESK_BLOCK_HEADER_CLASS}>
          <DispatchDeskHeaderIconRow icon={Zap}>
            <CardTitle className={DISPATCH_DESK_BLOCK_TITLE_CLASS}>Quick actions</CardTitle>
          </DispatchDeskHeaderIconRow>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-2 px-4 pt-0 md:px-6">
          {actions.map((action) => {
            const Icon = action.icon;
            const buttonProps = {
              disabled: actionsDisabled,
              variant: "outline" as const,
              size: "sm" as const,
              className: ACTION_BUTTON_CLASS,
            };

            if (action.href) {
              return (
                <Button
                  key={action.key}
                  {...buttonProps}
                  render={<Link to={action.href} />}
                >
                  <Icon className="size-3.5 shrink-0 text-primary" />
                  <span className={cn("truncate font-normal", DISPATCH_DESK_CONTENT_TEXT_CLASS)}>
                    {action.label}
                  </span>
                </Button>
              );
            }

            return (
              <Button key={action.key} {...buttonProps} onClick={action.onClick}>
                <Icon className="size-3.5 shrink-0 text-primary" />
                <span className={cn("truncate font-normal", DISPATCH_DESK_CONTENT_TEXT_CLASS)}>
                  {action.label}
                </span>
              </Button>
            );
          })}
        </CardContent>
      </Card>

      {modalKey === "dar-sheet" ? (
        <DarSheet
          isVisible
          darId={null}
          dar={null}
          draftRestoreValues={null}
          onDraftRestored={undefined}
          onClose={onCloseModal}
        />
      ) : null}
      {modalKey === "incident-sheet" ? <IncidentSheet isVisible onClose={onCloseModal} /> : null}
      {modalKey === "dispatch-log-sheet" ? (
        <DispatchLogsSheet isVisible dispatchLogId={null} onClose={onCloseModal} />
      ) : null}
      <LocationRequiredDialog {...locationDialogProps} />
    </>
  );
}
